<template>
  <loader :is-loading="getAllIsLoading" />
  <view-wrapper-v2
    :title="t('AI_AGENTS.TITLE')"
    :description="t('AI_AGENTS.DESCRIPTION')"
    class="h-full"
  >
    <template #action>
      <div class="flex h-10 space-x-4">
        <div v-if="aIAgentsData.length || searchTerm">
          <search-input v-model="searchTerm" no-padding />
        </div>
        <primary-button
          data-cy="new-agent-button"
          @click="showCreateModal = true"
        >
          {{ `${t("COMMON.CREATE")} ${t("AI_AGENTS.ONE")}` }}
        </primary-button>
      </div>
    </template>
    <div
      v-if="aIAgentsData.length || searchTerm"
      class="relative w-full space-y-2 pb-0"
    >
      <lf-table
        class="h-85-screen pb-4 2xl:h-90-screen"
        scrollable-element-id="appWrapper"
        :columns="columns"
        :metadata="aIAgentsMeta"
        :data="aIAgentsData"
        :pagination-absolute="false"
        :is-table-loading="isTableLoading"
        header-sticky
        @change-page="changePage"
        @change-items-per-page="changeItemsPerPage"
      />
    </div>
    <div
      v-if="!aIAgentsData.length && !getAllIsLoading"
      class="flex h-full w-full flex-col items-center justify-center"
    >
      <icon-base width="120" height="120" :icon="IconAI" />
      <lf-h3 class="my-6">
        {{ $t("AI_AGENTS.NO_RESULTS_TITLE") }}
      </lf-h3>
    </div>

    <ai-agents-edit-modal
      v-if="showCreateModal"
      @close="() => (showCreateModal = false)"
    />
  </view-wrapper-v2>
</template>

<script setup lang="ts">
import { onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { AgentFeatureType } from "@/enums/ai";
import { Ability, PermissionSubject } from "@/enums/auth";
import { usePermissions } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import { useFilters } from "@/hooks/filters";
import { useNotification } from "@/hooks/notifications";
import router from "@/router";
import { useAiAgentsStore } from "@/stores/ai/agents";

import IconAI from "@/components/icons/IconAI.vue";
import Loader from "@/components/ui/Loader.vue";
import ViewWrapperV2 from "@/components/ui/ViewWrapperV2.vue";
import PrimaryButton from "@/components/ui/buttons/PrimaryButton.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import LfTable from "@/components/ui/table/LfTable.vue";
import AiAgentsEditModal from "@/views/AIAgents/AIAgentModal.vue";
import AIAgentsTableActionButton from "@/views/AIAgents/AIAgentsTableActionButton.vue";

import type { IDealsColumn } from "@/models/applications";

const { t } = useI18n();
const aiAgentsStore = useAiAgentsStore();
const { checkPermissions } = usePermissions();
const { showMessage } = useNotification();

const {
  aiAgents: aIAgentsData,
  searchMeta: aIAgentsMeta,
  getAllIsLoading,
  options: aiAgentsOptions,
} = storeToRefs(aiAgentsStore);

const {
  searchTerm,
  filterStateAsSearchParams,
  changePage,
  changeItemsPerPage,
} = useFilters({
  additionalFilters: { client_ids: [] },
});

const showCreateModal = ref(false);

const scrollToTop = () => document.getElementById("appWrapper")?.scrollTo(0, 0);

const { fetchWrapper: getFilteredAIAgents, loading: isTableLoading } =
  usePromiseWrapper(
    async () => {
      scrollToTop();
      await aiAgentsStore.getAll({
        ...filterStateAsSearchParams.value,
        feature: AgentFeatureType.CommandBarAgentSearch,
      });
    },
    {
      onError: () => {
        // noop to catch validation
        return;
      },
    }
  );

const columns = [
  {
    label: t("COMMON.NAME"),
    key: "name",
  },
  {
    label: t("COMMON.TYPE"),
    key: "type",
    formatter: (type: string) =>
      aiAgentsOptions.value?.definition.agent_types[type] ?? "-",
  },
  {
    label: t("COMMON.DESCRIPTION"),
    key: "description",
  },
  {
    label: t("COMMON.ACTIONS"),
    key: "",
    component: AIAgentsTableActionButton,
  },
] satisfies IDealsColumn[];

const { fetchWrapper: checkPermission } = usePromiseWrapper(async () => {
  const canViewAIAgents = await checkPermissions(
    PermissionSubject.aiAgent,
    Ability.viewAny,
    {}
  );
  if (!canViewAIAgents) {
    showMessage(t("COMMON.ACCESS_RESTRICTED"), "error");
    router.push({ name: "Home" });
    return;
  }
});

watch(
  filterStateAsSearchParams,
  () => {
    getFilteredAIAgents();
  },
  { immediate: true }
);

onBeforeMount(async () => {
  await checkPermission();
});
</script>
